import { zodResolver } from '@hookform/resolvers/zod';
import { format, isValid, parseISO } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useTranslate } from '@i18n';
import { useSwing } from '@queries';
import { activityAtom } from '@store';
import { Button, Inline, Input, Modal, Stack, u } from '@ui';

const swingNameSchema = z.object({
    name: z.string().trim().max(20, 'Swing name must be 20 characters or less'),
});

type SwingNameForm = z.infer<typeof swingNameSchema>;

const getDefaultSwingName = (clubName: string | undefined, createdAt: string) => {
    const date = parseISO(createdAt);
    if (!isValid(date)) {
        return clubName || 'Swing';
    }

    const formattedDate = format(date, 'dd/MM/yy HH:mm');

    return `${clubName || 'Swing'} - ${formattedDate}`;
};

type SwingNameModalProps = {
    isOpen: boolean;
    swingUuid: string;
    setIsOpen: (val: boolean) => void;
    onSubmit: (val: string) => void;
    clubName?: string;
};

export const SwingNameModal = ({ isOpen, swingUuid, setIsOpen, onSubmit, clubName }: SwingNameModalProps) => {
    const t = useTranslate();
    const { data: swing } = useSwing(swingUuid);
    const activity = useAtomValue(activityAtom);
    const defaultSwingName = swing ? getDefaultSwingName(activity?.club?.longName ?? clubName, swing.createdAt) : '';
    const { register, handleSubmit, formState } = useForm<SwingNameForm>({
        resolver: zodResolver(swingNameSchema),
        values: {
            name: swing?.name ?? defaultSwingName,
        },
    });

    const handleCancel = () => {
        setIsOpen(false);
    };

    const handleSave = ({ name }: SwingNameForm) => {
        onSubmit(name || defaultSwingName);
        setIsOpen(false);
    };

    return (
        <Modal
            open={isOpen}
            title={t('generic_ui.enter_swing_name')}
            variant="partial"
            onDismiss={handleCancel}
            footer={{
                primary: (
                    <Inline>
                        <Button variant="secondary" onClick={handleCancel}>
                            {t('generic_ui.cancel')}
                        </Button>
                        <Button type="submit" form="swing-name-form">
                            {t('generic_ui.save')}
                        </Button>
                    </Inline>
                ),
            }}
        >
            <form id="swing-name-form" onSubmit={handleSubmit(handleSave)}>
                <Stack gap={u(2.5)}>
                    <Input
                        {...register('name')}
                        maxLength={20}
                        type="text"
                        placeholder={defaultSwingName}
                        error={formState.errors.name?.message}
                    />
                </Stack>
            </form>
        </Modal>
    );
};
